<script setup>
import {businessContact} from '@/utils/contact'
import {computed, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import {useHead} from '@vueuse/head'
import axios from 'axios'
import HomeHeader from '@/components/HomeHeader.vue'
import Footer from '@/components/Footer.vue'
import MarketingInquiryForm from '@/components/MarketingInquiryForm.vue'
import {useI18n} from '@/i18n'
import {applyImageFallback, estimateReadingMinutes, excerptFromHtml, formatLocalizedDate, itemsFrom} from '@/utils/detailContent'

const route = useRoute()
const {currentLanguage, t} = useI18n()
const detail = ref(null)
const relatedBlogs = ref([])
const loading = ref(true)
const error = ref(false)

const readingMinutes = computed(() => estimateReadingMinutes(detail.value?.content))
const publishedDate = computed(() => formatLocalizedDate(detail.value?.updated_at, currentLanguage.value))
const pageContext = computed(() => `${t('page.blog')}: ${detail.value?.blog_title || route.params.str}`)
const shareLinks = computed(() => {
  const url = encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')
  const title = encodeURIComponent(detail.value?.blog_title || '')
  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    email: `mailto:?subject=${title}&body=${url}`
  }
})

async function loadBlog(slug) {
  loading.value = true
  error.value = false
  relatedBlogs.value = []
  try {
    const detailResponse = await axios.get('/api/blogs/', {params: {site: slug}})
    detail.value = itemsFrom(detailResponse)[0] || null
    if (!detail.value) {
      error.value = true
      return
    }
    loading.value = false

    try {
      const relatedResponse = await axios.get('/api/blogs/')
      relatedBlogs.value = itemsFrom(relatedResponse).filter((item) => item.id !== detail.value.id).slice(0, 3)
    } catch (relatedError) {
      relatedBlogs.value = []
    }
  } catch (requestError) {
    error.value = true
  } finally {
    loading.value = false
  }
}

watch(() => route.params.str, (slug) => loadBlog(slug), {immediate: true})

useHead({
  title: () => detail.value?.blog_title || t('page.blog'),
  meta: [{name: 'description', content: () => excerptFromHtml(detail.value?.content, 170)}]
})
</script>

<template>
  <div class="page mkt-page mkt-detail-page">
    <HomeHeader/>
    <main>
      <section v-if="loading" class="mkt-detail-state">
        <i class="mdi mdi-loading mdi-spin" aria-hidden="true"></i><p>{{ t('marketing.detail.common.loading') }}</p>
      </section>
      <section v-else-if="error || !detail" class="mkt-detail-state is-error">
        <i class="mdi mdi-alert-circle-outline" aria-hidden="true"></i><h1>{{ t('marketing.detail.common.notFound') }}</h1>
        <a class="mkt-button mkt-button-primary" href="/blogs">{{ t('page.blogs') }}</a>
      </section>

      <template v-else>
        <section class="mkt-blog-detail-hero">
          <img class="mkt-case-detail-image" :src="detail.image_1" :alt="detail.blog_title" @error="applyImageFallback">
          <div class="mkt-case-detail-shade"></div>
          <div class="mkt-shell mkt-blog-detail-inner">
            <nav class="mkt-detail-breadcrumb is-light" aria-label="Breadcrumb">
              <a href="/">{{ t('common.home') }}</a><i class="mdi mdi-chevron-right"></i>
              <a href="/blogs">{{ t('page.blogs') }}</a><i class="mdi mdi-chevron-right"></i>
              <span>{{ detail.blog_title }}</span>
            </nav>
            <div>
              <span class="mkt-eyebrow is-light">{{ t('marketing.detail.blog.eyebrow') }}</span>
              <h1>{{ detail.blog_title }}</h1>
              <div class="mkt-blog-detail-meta">
                <span><i class="mdi mdi-calendar"></i>{{ t('marketing.detail.blog.updated') }} {{ publishedDate }}</span>
                <span><i class="mdi mdi-clock-outline"></i>{{ readingMinutes }} {{ t('marketing.detail.blog.minRead') }}</span>
              </div>
            </div>
          </div>
        </section>

        <section class="mkt-section mkt-blog-article-section">
          <div class="mkt-shell mkt-blog-reading-layout">
            <article class="mkt-blog-article">
              <span class="mkt-eyebrow">{{ t('marketing.detail.blog.articleEyebrow') }}</span>
              <h2>{{ detail.blog_title }}</h2>
              <img class="mkt-blog-feature-image" :src="detail.image_1" :alt="detail.blog_title" @error="applyImageFallback">
              <div class="mkt-rich-content mkt-article-content" v-html="detail.content"></div>
              <div class="mkt-blog-share">
                <strong>{{ t('marketing.detail.blog.share') }}</strong>
                <div>
                  <a :href="shareLinks.facebook" target="_blank" rel="noopener" aria-label="Facebook"><i class="mdi mdi-facebook"></i></a>
                  <a :href="shareLinks.linkedin" target="_blank" rel="noopener" aria-label="LinkedIn"><i class="mdi mdi-linkedin"></i></a>
                  <a :href="shareLinks.email" aria-label="Email"><i class="mdi mdi-email-outline"></i></a>
                </div>
              </div>
            </article>

            <aside class="mkt-blog-project-brief">
              <span>{{ t('marketing.detail.blog.sidebarTitle') }}</span>
              <h2>{{ t('marketing.detail.blog.sidebarTitle') }}</h2>
              <p>{{ t('marketing.detail.blog.sidebarText') }}</p>
              <ul><li v-for="point in t('marketing.detail.blog.sidebarPoints')" :key="point"><i class="mdi mdi-check"></i>{{ point }}</li></ul>
              <a class="mkt-button mkt-button-accent" href="#detail-inquiry">{{ t('marketing.detail.common.quote') }}</a>
              <a class="mkt-blog-whatsapp-link" :href="businessContact.whatsappHref" target="_blank" rel="noopener"><i class="mdi mdi-whatsapp"></i>{{ t('marketing.detail.common.whatsapp') }}</a>
            </aside>
          </div>
        </section>

        <section v-if="relatedBlogs.length" class="mkt-section mkt-detail-related-section">
          <div class="mkt-shell">
            <div class="mkt-detail-section-header">
              <div><span class="mkt-eyebrow">{{ t('marketing.detail.blog.relatedEyebrow') }}</span><h2>{{ t('marketing.detail.blog.relatedTitle') }}</h2></div>
              <a class="mkt-text-link" href="/blogs">{{ t('marketing.detail.common.viewAll') }}<i class="mdi mdi-arrow-right"></i></a>
            </div>
            <div class="mkt-detail-related-grid is-blog">
              <article v-for="item in relatedBlogs" :key="item.id">
                <a :href="`/blogs/${encodeURIComponent(item.site || item.id)}`">
                  <img :src="item.image_1" :alt="item.blog_title" loading="lazy" @error="applyImageFallback">
                  <div><span>{{ formatLocalizedDate(item.updated_at, currentLanguage) }}</span><h3>{{ item.blog_title }}</h3><i class="mdi mdi-arrow-top-right"></i></div>
                </a>
              </article>
            </div>
          </div>
        </section>

        <section id="detail-inquiry" class="mkt-inquiry-section mkt-detail-inquiry-section">
          <div class="mkt-shell mkt-inquiry-layout">
            <div class="mkt-inquiry-copy">
              <span class="mkt-eyebrow is-light">{{ t('marketing.detail.blog.inquiryEyebrow') }}</span>
              <h2>{{ t('marketing.detail.blog.inquiryTitle') }}</h2>
              <p>{{ t('marketing.detail.blog.inquiryText') }}</p>
              <ul class="mkt-check-list is-light"><li v-for="item in t('marketing.home.inquiry.benefits')" :key="item"><i class="mdi mdi-check"></i>{{ item }}</li></ul>
            </div>
            <MarketingInquiryForm :context="pageContext"/>
          </div>
        </section>
      </template>
    </main>
    <Footer/>
  </div>
</template>
